/**
 * Condition pages (/conditions/:slug) — "who treats this in Canberra".
 *
 * Patients search by problem ("knee specialist Canberra", "ADHD assessment
 * Canberra"), not by specialty name. Each entry routes the search to the
 * specialty pages that list real doctors.
 *
 * Rules: general information only. Always "see your GP first" (referral +
 * Medicare rebate); never treatment, dosing or outcome claims; no "best"
 * (AHPRA s133). `reviewedBy` stays null until a clinician has read the
 * entry — the prerender logs unreviewed entries on every build.
 *
 * `specialties` are API specialty slugs (lib/specialty-slug.ts), most
 * common referral first.
 */
export interface ConditionContent {
  slug: string;
  /** Lower-case display name used in sentences, e.g. "knee pain". */
  name: string;
  /** H1 and title stem — the phrase patients search for. */
  heading: string;
  summary: string;
  specialties: string[];
  /** Official pages the summary is checked against (scripts/review-copy.mjs).
   *  Empty = no official source found yet, so the page stays out of Google. */
  sources: string[];
  reviewedBy: string | null;
}

export const CONDITIONS: ConditionContent[] = [
  {
    slug: "knee-pain",
    name: "knee pain",
    heading: "Knee specialists in Canberra",
    summary:
      "Knee pain from injury, arthritis or wear is usually assessed first by your GP, often with an X-ray or scan and physiotherapy. If it does not settle, or surgery may be needed, your GP may refer you to an orthopaedic surgeon.",
    specialties: ["orthopaedic-surgery", "rheumatology"],
    sources: ["https://www.healthdirect.gov.au/symptoms/aches-and-pains/knee-pain"],
    reviewedBy: null,
  },
  {
    slug: "hip-pain",
    name: "hip pain",
    heading: "Hip specialists in Canberra",
    summary:
      "See your doctor if you have hip pain. Your doctor or physiotherapist can work out what is causing it, and your doctor may refer you to an orthopaedic surgeon if you need surgery or other specialist treatment.",
    specialties: ["orthopaedic-surgery", "rheumatology"],
    sources: ["https://www.healthdirect.gov.au/symptoms/aches-and-pains/hip-pain", "https://www.betterhealth.vic.gov.au/health/serviceprofiles/Orthopaedic-Surgeon", "https://www.betterhealth.vic.gov.au/health/serviceprofiles/Rheumatologist"],
    reviewedBy: null,
  },
  {
    slug: "shoulder-pain",
    name: "shoulder pain",
    heading: "Shoulder specialists in Canberra",
    summary:
      "Shoulder problems such as rotator cuff injuries, frozen shoulder and dislocations are usually assessed by your GP first. Physiotherapy can help, and sometimes surgery is needed to correct structural problems.",
    specialties: ["orthopaedic-surgery"],
    sources: ["https://www.healthdirect.gov.au/symptoms/aches-and-pains/shoulder-pain"],
    reviewedBy: null,
  },
  {
    slug: "back-pain",
    name: "back pain",
    heading: "Back pain specialists in Canberra",
    summary:
      "Your doctor can give you advice about steps you can take to relieve and manage back pain. See your doctor if you have muscle weakness or numbness in your arms or legs, changes to your bladder or bowel function, or unexplained weight loss.",
    specialties: ["pain-medicine", "orthopaedic-surgery", "neurosurgery"],
    sources: ["https://www.healthdirect.gov.au/symptoms/aches-and-pains/back-pain"],
    reviewedBy: null,
  },
  {
    slug: "skin-cancer-check",
    name: "skin cancer and suspicious moles",
    heading: "Skin cancer specialists in Canberra",
    summary:
      "Your GP can check a mole or spot that has changed. If a skin cancer is suspected, your doctor or a dermatologist may take a biopsy. Many skin cancers can be removed by your doctor.",
    specialties: ["dermatology", "plastic-and-reconstructive-surgery"],
    sources: ["https://www.healthdirect.gov.au/conditions/cancer/skin-cancer-and-melanomas", "https://www.healthdirect.gov.au/conditions/skin-conditions/moles"],
    reviewedBy: null,
  },
  {
    slug: "eczema-psoriasis",
    name: "eczema and psoriasis",
    heading: "Eczema and psoriasis specialists in Canberra",
    summary:
      "Eczema and psoriasis are usually managed by your GP first. If they are severe or not responding to treatment, your GP may refer you to a dermatologist.",
    specialties: ["dermatology"],
    sources: ["https://www.healthdirect.gov.au/conditions/skin-conditions/eczema", "https://www.healthdirect.gov.au/conditions/skin-conditions/psoriasis", "https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/dermatologist"],
    reviewedBy: null,
  },
  {
    slug: "colonoscopy",
    name: "colonoscopy",
    heading: "Colonoscopy in Canberra",
    summary:
      "A colonoscopy is arranged through a referral, usually from your GP, to a specialist such as a gastroenterologist. Common reasons include a positive bowel cancer screening test, bleeding, or a change in bowel habit.",
    specialties: ["gastroenterology-and-hepatology"],
    sources: ["https://www.healthdirect.gov.au/procedures/diagnostic-tests-and-scans/colonoscopy"],
    reviewedBy: null,
  },
  {
    slug: "positive-bowel-screening-test",
    name: "a positive bowel cancer screening test",
    heading: "Positive bowel screening test: next steps in Canberra",
    summary:
      "If your National Bowel Cancer Screening Program test is positive, see your GP promptly. They may ask you to do more tests, such as a colonoscopy, which lets a doctor look at the inside of your bowel using a camera. A positive test does not mean you have cancer, but it does need follow-up.",
    specialties: ["gastroenterology-and-hepatology"],
    sources: ["https://www.healthdirect.gov.au/procedures/health-screenings/bowel-cancer-screening"],
    reviewedBy: null,
  },
  {
    slug: "reflux-heartburn",
    name: "reflux and heartburn",
    heading: "Reflux and heartburn specialists in Canberra",
    summary:
      "Reflux can often be managed with lifestyle changes and medicines. If your symptoms do not improve with medicines, or could be caused by another condition, your doctor may arrange an endoscopy.",
    specialties: ["gastroenterology-and-hepatology"],
    sources: ["https://www.healthdirect.gov.au/conditions/gastro-intestinal-illnesses/gord-reflux", "https://www.healthdirect.gov.au/symptoms/heartburn"],
    reviewedBy: null,
  },
  {
    slug: "palpitations",
    name: "palpitations",
    heading: "Palpitations and heart rhythm specialists in Canberra",
    summary:
      "Palpitations are a feeling of a racing, pounding or irregular heartbeat. Your doctor may suggest tests, such as an electrocardiogram (ECG), which measures your heart's electrical activity. Your doctor may also refer you to a cardiologist or other specialists for more tests. Call triple zero (000) and ask for an ambulance if you have palpitations along with chest pain, shortness of breath, dizziness or fainting.",
    specialties: ["cardiology"],
    sources: ["https://www.healthdirect.gov.au/symptoms/heart-palpitations"],
    reviewedBy: null,
  },
  {
    slug: "thyroid",
    name: "thyroid conditions",
    heading: "Thyroid specialists in Canberra",
    summary:
      "Your doctor will ask about your symptoms, examine you and may suggest blood tests. For more complex thyroid problems, or a thyroid lump, your GP may refer you to an endocrinologist, and sometimes to a surgeon.",
    specialties: ["endocrinology", "ent-ear-nose-and-throat"],
    sources: ["https://www.healthdirect.gov.au/conditions/hormonal-diseases/thyroid-problems", "https://www.healthdirect.gov.au/conditions/hormonal-diseases/goitre", "https://www.healthdirect.gov.au/conditions/cancer/thyroid-cancer"],
    reviewedBy: null,
  },
  {
    slug: "diabetes",
    name: "diabetes",
    heading: "Diabetes specialists in Canberra",
    summary:
      "Your GP can diagnose and treat diabetes. When specialist help is needed, your GP may refer you to an endocrinologist, a specialist who treats conditions such as diabetes and thyroid problems.",
    specialties: ["endocrinology"],
    sources: ["https://www.healthdirect.gov.au/conditions/metabolic-diseases/diabetes", "https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/endocrinologist"],
    reviewedBy: null,
  },
  {
    slug: "migraine-headache",
    name: "migraine and headaches",
    heading: "Migraine and headache specialists in Canberra",
    summary:
      "See your doctor if your headaches are frequent, getting worse or changing. Your doctor may refer you to a neurologist. For a sudden, severe headache, call triple zero (000).",
    specialties: ["neurology"],
    sources: ["https://www.healthdirect.gov.au/symptoms/aches-and-pains/headaches", "https://www.healthdirect.gov.au/conditions/brain-diseases/migraine"],
    reviewedBy: null,
  },
  {
    slug: "epilepsy-seizures",
    name: "epilepsy and seizures",
    heading: "Epilepsy and seizure specialists in Canberra",
    summary:
      "Speak to your doctor if you think you have had a seizure. You will likely be referred to a neurologist (or, for a child, a paediatrician) for tests, which may include an EEG to measure brain activity and an MRI to take a picture of the brain.",
    specialties: ["neurology"],
    sources: ["https://www.healthdirect.gov.au/conditions/brain-diseases/epilepsy", "https://www.healthdirect.gov.au/conditions/brain-diseases/seizures"],
    reviewedBy: null,
  },
  {
    slug: "nerve-conduction-study",
    name: "nerve conduction studies",
    heading: "Nerve conduction studies (EMG) in Canberra",
    summary:
      "Nerve conduction studies measure the electrical activity in your nerves, for example to check for carpal tunnel syndrome or nerve damage. They need a referral, usually from your GP.",
    specialties: ["neurology"],
    sources: ["https://www.healthdirect.gov.au/conditions/brain-diseases/neuropathy", "https://www.healthdirect.gov.au/symptoms/aches-and-pains/nerve-pain", "https://www.healthdirect.gov.au/conditions/joint-and-muscle-diseases/carpal-tunnel-syndrome"],
    reviewedBy: null,
  },
  {
    slug: "adhd-assessment",
    name: "ADHD assessment",
    heading: "ADHD assessment in Canberra",
    summary:
      "An ADHD assessment for adults or children usually starts with your GP, who can refer you to a psychiatrist (or, for children, a paediatrician).",
    specialties: ["psychiatry"],
    sources: ["https://www.healthdirect.gov.au/conditions/disability-and-developmental-disorders/attention-deficit-disorder-add-or-adhd"],
    reviewedBy: null,
  },
  {
    slug: "memory-problems",
    name: "memory problems",
    heading: "Memory and dementia assessment in Canberra",
    summary:
      "If you or a family member are worried about memory or thinking, start with your GP. They may refer you to a geriatrician or neurologist for a detailed assessment.",
    specialties: ["geriatrics-general-medicine", "neurology"],
    sources: ["https://www.healthdirect.gov.au/conditions/brain-diseases/memory-disorders", "https://www.healthdirect.gov.au/conditions/dementia/dementia-overview", "https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/geriatrician", "https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/neurologist", "https://www.healthdirect.gov.au/healthy-living/emotional-wellbeing/worried-about-your-memory"],
    reviewedBy: null,
  },
  {
    slug: "kidney-disease",
    name: "kidney disease",
    heading: "Kidney specialists in Canberra",
    summary:
      "Chronic kidney disease is often found on routine blood and urine tests and is usually managed by your GP. Your GP may refer you to a nephrologist (kidney specialist) if kidney function is falling or there is protein or blood in the urine.",
    specialties: ["nephrology"],
    sources: ["https://www.healthdirect.gov.au/conditions/kidney-and-urinary-problems/chronic-kidney-disease"],
    reviewedBy: null,
  },
  {
    slug: "kidney-stones",
    name: "kidney stones",
    heading: "Kidney stone specialists in Canberra",
    summary:
      "Your doctor may order blood and urine tests and imaging such as a CT scan, ultrasound or x-ray. Your doctor may refer you to a urologist for treatment. Severe pain with fever needs urgent care.",
    specialties: ["urology"],
    sources: ["https://www.healthdirect.gov.au/conditions/kidney-and-urinary-problems/kidney-stones"],
    reviewedBy: null,
  },
  {
    slug: "raised-psa",
    name: "a raised PSA",
    heading: "Raised PSA and prostate specialists in Canberra",
    summary:
      "A PSA blood test can be used to test for prostate cancer. If your PSA is raised, your doctor may recommend repeating the test, other tests to find the cause, or a biopsy or MRI to check for prostate cancer.",
    specialties: ["urology"],
    sources: ["https://www.healthdirect.gov.au/procedures/diagnostic-tests-and-scans/blood-tests/prostate-specific-antigen-PSA-test", "https://www.healthdirect.gov.au/healthcare-and-services/roles-and-specialties/urologist"],
    reviewedBy: null,
  },
  {
    slug: "gout",
    name: "gout",
    heading: "Gout specialists in Canberra",
    summary:
      "Gout is usually managed by your GP. For frequent attacks or gout that is hard to control, your GP may refer you to a rheumatologist.",
    specialties: ["rheumatology"],
    sources: ["https://www.healthdirect.gov.au/conditions/joint-and-muscle-diseases/gout", "https://www.betterhealth.vic.gov.au/health/serviceprofiles/Rheumatologist"],
    reviewedBy: null,
  },
  {
    slug: "rheumatoid-arthritis",
    name: "rheumatoid arthritis",
    heading: "Rheumatoid arthritis specialists in Canberra",
    summary:
      "Joint swelling and stiffness that suggests inflammatory arthritis should be assessed by your GP, who may refer you to a rheumatologist.",
    specialties: ["rheumatology"],
    sources: ["https://www.healthdirect.gov.au/conditions/joint-and-muscle-diseases/rheumatoid-arthritis", "https://www.betterhealth.vic.gov.au/health/conditionsandtreatments/rheumatoid-arthritis", "https://www.betterhealth.vic.gov.au/health/serviceprofiles/Rheumatologist"],
    reviewedBy: null,
  },
  {
    slug: "anaemia",
    name: "anaemia",
    heading: "Anaemia and blood specialists in Canberra",
    summary:
      "Anaemia can be diagnosed with a blood test. Your doctor can refer you for tests to work out its cause, and you might see a specialist, depending on your symptoms and test results.",
    specialties: ["haematology", "gastroenterology-and-hepatology"],
    sources: ["https://www.healthdirect.gov.au/conditions/blood-disorders/anaemia"],
    reviewedBy: null,
  },
  {
    slug: "varicose-veins",
    name: "varicose veins",
    heading: "Varicose vein specialists in Canberra",
    summary:
      "For varicose veins, your doctor may refer you to a vascular surgeon for treatment.",
    specialties: ["vascular-surgery"],
    sources: ["https://www.healthdirect.gov.au/conditions/vascular-diseases/varicose-veins"],
    reviewedBy: null,
  },
  {
    slug: "snoring-sleep-apnoea",
    name: "snoring and sleep apnoea",
    heading: "Snoring and sleep apnoea specialists in Canberra",
    summary:
      "If you snore loudly or stop breathing during sleep, see your GP. They may arrange a sleep study or refer you to a specialist.",
    specialties: ["ent-ear-nose-and-throat"],
    sources: ["https://www.healthdirect.gov.au/conditions/sleep-disorders/how-to-stop-snoring", "https://www.healthdirect.gov.au/conditions/sleep-disorders/obstructive-sleep-apnoea"],
    reviewedBy: null,
  },
  {
    slug: "hearing-loss",
    name: "hearing loss",
    heading: "Hearing loss and ear specialists in Canberra",
    summary:
      "Hearing loss is often checked first with your GP and an audiologist. Your GP may refer you to an ear, nose and throat (ENT) specialist for further assessment. Sudden hearing loss needs prompt care.",
    specialties: ["ent-ear-nose-and-throat"],
    sources: ["https://www.healthdirect.gov.au/conditions/ear-diseases/hearing-loss", "https://www.healthdirect.gov.au/procedures/diagnostic-tests-and-scans/hearing-test"],
    reviewedBy: null,
  },
];

export function conditionBySlug(slug: string): ConditionContent | null {
  return CONDITIONS.find((condition) => condition.slug === slug) ?? null;
}

/** Conditions that route to a specialty — for "Common conditions" links on specialty pages. */
export function conditionsForSpecialty(specialtySlug: string): ConditionContent[] {
  return CONDITIONS.filter((condition) => condition.specialties.includes(specialtySlug));
}

export const CONDITION_NOTE =
  "Start with your GP: they can assess you, arrange tests and write the referral you need for a Medicare rebate. General information only, not medical advice.";
